/**
 * Marketing Kit — Pro-only print & social material generator.
 *
 * Renders branded templates (flyer, counter sign, social square, story,
 * QR sticker sheet) live from the owner's profile data + a REAL scannable
 * QR code served by the backend (/api/marketing-kit/qr.png). Downloads
 * are generated fully in-browser: html2canvas → PNG, jsPDF → print PDF.
 *
 * Backend: routers/marketing_kit.py
 *  GET /api/marketing-kit/trucks   — owner's trucks + is_pro
 *  GET /api/marketing-kit/data     — Pro-gated template data (403 pro_required)
 *  PUT /api/marketing-kit/prefs    — persist accent/headline/material/style
 *  GET /api/marketing-kit/qr.png   — real QR (auth required)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import api, { API, getAuthToken } from "../lib/api";
import { BACKEND_BASE } from "../lib/backendBase";
import { useAuth } from "../lib/auth";
import { trackSiteEvent } from "../lib/analytics";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, CircleNotch, DownloadSimple, FilePdf, LockKey,
  Megaphone, Palette, QrCode, Sparkle,
} from "@phosphor-icons/react";

/* ------------------------------------------------------------------ */
/* Config                                                              */
/* ------------------------------------------------------------------ */

const MATERIALS = [
  { key: "flyer", label: "Flyer", sub: '8.5\u00d711" print', w: 816, h: 1056, pdf: [8.5, 11], scale: 3, styles: ["scan", "tracker", "bold", "photo", "clean"] },
  { key: "sign", label: "Counter sign", sub: '5\u00d77" print', w: 480, h: 672, pdf: [5, 7], scale: 4, styles: ["bold", "photo", "clean"] },
  { key: "square", label: "Social post", sub: "1080\u00d71080 \u00b7 IG / FB", w: 540, h: 540, pdf: null, scale: 2, styles: ["photo", "bold", "clean"] },
  { key: "story", label: "Story", sub: "1080\u00d71920 \u00b7 IG story", w: 540, h: 960, pdf: null, scale: 2, styles: ["photo", "bold", "clean"] },
  { key: "stickers", label: "Sticker sheet", sub: '12 QR stickers \u00b7 8.5\u00d711"', w: 816, h: 1056, pdf: [8.5, 11], scale: 3, styles: ["accent", "light"] },
];
const MAT_BY_KEY = Object.fromEntries(MATERIALS.map((m) => [m.key, m]));

const STYLE_LABELS = { scan: "Scan Me", tracker: "App Dark", photo: "Photo", bold: "Bold", clean: "Clean", accent: "Color", light: "Light" };

const ACCENTS = ["#DC2626", "#EA580C", "#CA8A04", "#16A34A", "#0E7490", "#4F46E5", "#0F172A"];

const DISPLAY = "'Outfit', system-ui, sans-serif";
const TALL = "'Bebas Neue', 'Outfit', sans-serif";

const fit = (base, text, threshold = 16) => {
  const len = (text || "").length;
  if (len <= threshold) return base;
  return Math.max(Math.round(base * 0.55), Math.round(base * (threshold / len) * 1.15));
};

async function toDataUrl(url) {
  // Cross-origin images (e.g. prod-hosted logos) send ACAO:* — browsers
  // reject credentialed requests against wildcard CORS, so only attach
  // auth/cookies for our own backend URLs.
  const own = url.startsWith("/") || url.startsWith(BACKEND_BASE);
  const token = own ? getAuthToken() : null;
  const res = await fetch(url, {
    credentials: own ? "include" : "omit",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

const igHandle = (v) => (v || "")
  .trim()
  .replace(/^@/, "")
  .replace(/^https?:\/\/(www\.)?instagram\.com\//i, "")
  .replace(/[/?#].*$/, "");

const slugify = (s) => (s || "truck").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

/* ------------------------------------------------------------------ */
/* Template building blocks                                            */
/* ------------------------------------------------------------------ */

const Logo = ({ src, size, ring = "#fff" }) =>
  src ? (
    <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", border: `${Math.max(3, size * 0.04)}px solid ${ring}`, flexShrink: 0, background: "#fff", boxShadow: "0 4px 14px rgba(0,0,0,0.18)" }}>
      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} crossOrigin="anonymous" />
    </div>
  ) : null;

const QRCard = ({ qr, size, pad = 14, radius = 18 }) => (
  <div style={{ background: "#fff", padding: pad, borderRadius: radius, boxShadow: "0 6px 22px rgba(0,0,0,0.16)", display: "inline-block", lineHeight: 0 }}>
    {qr ? (
      <img src={qr} alt="QR code" style={{ width: size, height: size, display: "block" }} />
    ) : (
      <div style={{ width: size, height: size, display: "grid", placeItems: "center", background: "#f1f5f9", borderRadius: 8, color: "#94a3b8", fontFamily: DISPLAY, fontSize: 13, fontWeight: 600, textAlign: "center", lineHeight: 1.3 }}>
        QR loads after<br />publish
      </div>
    )}
  </div>
);

const Bullet = ({ accent, children, size = 17 }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: DISPLAY, fontSize: size, fontWeight: 600, color: "#1e293b" }}>
    <span style={{ width: 10, height: 10, background: accent, borderRadius: 3, flexShrink: 0 }} />
    {children}
  </div>
);

const Footer = ({ accent, url, dark = false, size = 15 }) => (
  <div style={{ background: dark ? "#0f172a" : accent, color: "#fff", padding: "13px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: DISPLAY }}>
    <span style={{ fontWeight: 800, fontSize: size, letterSpacing: 0.3 }}>{url || "thetrucktrackr.com"}</span>
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <img src="/icon-192.png" alt="" style={{ width: size + 9, height: size + 9, borderRadius: 6 }} />
      <span style={{ fontWeight: 800, fontSize: size - 1 }}>TruckTrackr</span>
    </span>
  </div>
);

/* CSS-drawn phone showing the truck's REAL TruckTrackr profile screen —
   app header, truck name + LIVE badge + Follow button, and their actual
   next scheduled stop. Pure DOM so html2canvas renders it crisp. */
const PhoneMock = ({ d, w = 250 }) => {
  const h = Math.round(w * 2.02);
  const f = (n) => Math.round(w * n);
  const ns = d.nextStop;
  return (
    <div style={{ width: w, height: h, background: "#0f172a", borderRadius: f(0.14), padding: f(0.032), boxShadow: "0 18px 40px rgba(15,23,42,0.32)", flexShrink: 0 }}>
      <div style={{ width: "100%", height: "100%", borderRadius: f(0.11), overflow: "hidden", position: "relative", display: "flex", flexDirection: "column", background: "#fff", fontFamily: DISPLAY }}>
        {/* notch + app header */}
        <div style={{ position: "absolute", top: 7, left: "50%", transform: "translateX(-50%)", width: f(0.3), height: f(0.05), background: "#0f172a", borderRadius: 999, zIndex: 2 }} />
        <div style={{ paddingTop: f(0.1), paddingBottom: f(0.035), display: "flex", alignItems: "center", justifyContent: "center", gap: 6, borderBottom: "1px solid #F1F5F9", flexShrink: 0 }}>
          <img src="/icon-192.png" alt="" style={{ width: f(0.075), height: f(0.075), borderRadius: f(0.02) }} />
          <span style={{ fontWeight: 800, fontSize: f(0.055), color: "#0f172a", letterSpacing: -0.2 }}>TruckTrackr</span>
        </div>
        {/* truck identity row */}
        <div style={{ display: "flex", alignItems: "center", gap: f(0.035), padding: `${f(0.045)}px ${f(0.055)}px 0` }}>
          <div style={{ width: f(0.17), height: f(0.17), borderRadius: "50%", overflow: "hidden", border: "2px solid #E2E8F0", flexShrink: 0, background: "#fff" }}>
            {d.logo
              ? <img src={d.logo} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", background: d.accent, display: "grid", placeItems: "center", fontWeight: 800, fontSize: f(0.08), color: "#fff" }}>{(d.name || "T").charAt(0)}</div>}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: f(0.058), color: "#0f172a", lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 3, background: "#DC2626", borderRadius: 999, padding: `2px ${f(0.03)}px` }}>
              <span style={{ width: f(0.02), height: f(0.02), borderRadius: "50%", background: "#fff" }} />
              <span style={{ fontWeight: 800, fontSize: f(0.035), color: "#fff", letterSpacing: 0.8 }}>LIVE</span>
            </div>
          </div>
          <div style={{ background: d.accent, color: "#fff", fontWeight: 700, fontSize: f(0.042), padding: `${f(0.018)}px ${f(0.05)}px`, borderRadius: 999, flexShrink: 0 }}>+ Follow</div>
        </div>
        {/* next stop card */}
        <div style={{ margin: `${f(0.045)}px ${f(0.055)}px 0`, background: "#F8FAFC", border: "1px solid #EDF2F7", borderRadius: f(0.045), padding: f(0.05) }}>
          <div style={{ fontWeight: 800, fontSize: f(0.034), color: d.accent, letterSpacing: 1.2, textTransform: "uppercase" }}>Next stop</div>
          <div style={{ fontWeight: 800, fontSize: f(0.05), color: "#0f172a", marginTop: 4, lineHeight: 1.2 }}>
            {ns ? (ns.event_name || ns.day_label) : "This week's schedule"}
          </div>
          {ns && ns.event_name ? (
            <div style={{ fontWeight: 600, fontSize: f(0.042), color: "#475569", marginTop: 2 }}>{ns.day_label}</div>
          ) : null}
          <div style={{ fontWeight: 700, fontSize: f(0.044), color: "#334155", marginTop: 4 }}>
            {ns ? ns.time_range : "Times, locations & menus"}
          </div>
          {ns && ns.address ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 5, marginTop: 5 }}>
              <span style={{ width: f(0.028), height: f(0.028), borderRadius: "50% 50% 50% 0", background: d.accent, transform: "rotate(-45deg)", marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontWeight: 600, fontSize: f(0.04), color: "#64748b", lineHeight: 1.3 }}>{ns.address}</span>
            </div>
          ) : null}
        </div>
        {/* directions CTA + schedule hint rows */}
        <div style={{ margin: `${f(0.04)}px ${f(0.055)}px 0`, background: d.accent, color: "#fff", fontWeight: 800, fontSize: f(0.046), textAlign: "center", padding: `${f(0.032)}px 0`, borderRadius: 999 }}>
          Get directions
        </div>
        <div style={{ margin: `${f(0.045)}px ${f(0.055)}px 0`, display: "flex", flexDirection: "column", gap: f(0.028) }}>
          <div style={{ height: f(0.032), width: "72%", background: "#EDF2F7", borderRadius: 999 }} />
          <div style={{ height: f(0.032), width: "88%", background: "#EDF2F7", borderRadius: 999 }} />
          <div style={{ height: f(0.032), width: "60%", background: "#EDF2F7", borderRadius: 999 }} />
        </div>
      </div>
    </div>
  );
};

/* Concentric attention rings behind a QR block (Walk-O-Taco style). */
const QRRings = ({ accent, size, children }) => (
  <div style={{ position: "relative", width: size, height: size, display: "grid", placeItems: "center" }}>
    <div style={{ position: "absolute", inset: -Math.round(size * 0.22), borderRadius: "50%", border: `3px solid ${accent}`, opacity: 0.14 }} />
    <div style={{ position: "absolute", inset: -Math.round(size * 0.12), borderRadius: "50%", border: `3px solid ${accent}`, opacity: 0.3 }} />
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* Templates — each renders at native pixel size                       */
/* ------------------------------------------------------------------ */

const FLYER_BULLETS = ["Live location map", "This week's schedule", "Menu, photos & hours", "Follow for arrival alerts"];

function Flyer({ style, d }) {
  const nameSize = fit(64, d.name, 16);
  if (style === "scan") {
    return (
      <div style={{ width: 816, height: 1056, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", top: -140, right: -140, width: 340, height: 340, borderRadius: "50%", background: d.accent, opacity: 0.09 }} />
        <div style={{ position: "absolute", top: -90, right: -90, width: 240, height: 240, borderRadius: "50%", border: `3px solid ${d.accent}`, opacity: 0.15 }} />
        {/* brand row — big truck logo for real-world branding */}
        <div style={{ display: "flex", alignItems: "center", gap: 20, padding: "34px 52px 0" }}>
          {d.logo ? <Logo src={d.logo} size={96} ring="#f1f5f9" /> : (
            <div style={{ width: 96, height: 96, borderRadius: "50%", background: d.accent, display: "grid", placeItems: "center", fontFamily: DISPLAY, fontWeight: 800, fontSize: 42, color: "#fff", flexShrink: 0 }}>{(d.name || "T").charAt(0)}</div>
          )}
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(38, d.name, 20), color: "#0f172a", letterSpacing: -0.8 }}>{d.name}</div>
        </div>
        {/* headline */}
        <div style={{ padding: "34px 52px 0" }}>
          <div style={{ fontFamily: TALL, fontSize: 118, lineHeight: 0.92, color: d.accent, letterSpacing: 2, textTransform: "uppercase" }}>Scan here</div>
          <div style={{ fontFamily: TALL, fontSize: 62, lineHeight: 1, color: "#0f172a", letterSpacing: 2, marginTop: 10, textTransform: "uppercase" }}>to find us & follow</div>
        </div>
        {/* QR + phone */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 64px 0 72px" }}>
          <QRRings accent={d.accent} size={330}>
            <div style={{ background: "#fff", border: `9px solid ${d.accent}`, borderRadius: 28, padding: 16, lineHeight: 0 }}>
              {d.qr ? <img src={d.qr} alt="QR code" style={{ width: 268, height: 268, display: "block" }} /> : <QRCard qr={null} size={268} pad={0} />}
            </div>
          </QRRings>
          <PhoneMock d={d} w={252} />
        </div>
        {/* tagline + TruckTrackr lockup */}
        <div style={{ padding: "26px 52px 24px", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(30, `Never miss ${d.name} again.`, 34), color: "#0f172a", letterSpacing: -0.4, textTransform: "uppercase" }}>
              Never miss {d.name} again.
            </div>
            <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: 20, color: "#64748b", marginTop: 6 }}>
              Live location · weekly schedule · follow for alerts
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, paddingBottom: 2 }}>
            <img src="/icon-192.png" alt="" style={{ width: 44, height: 44, borderRadius: 11 }} />
            <div style={{ fontFamily: DISPLAY, lineHeight: 1.05 }}>
              <div style={{ fontWeight: 800, fontSize: 21, color: "#0f172a", letterSpacing: -0.3 }}>TruckTrackr</div>
              <div style={{ fontWeight: 600, fontSize: 12.5, color: "#94a3b8" }}>Free · iPhone & Android</div>
            </div>
          </div>
        </div>
        <Footer accent={d.accent} url={d.url} size={17} />
      </div>
    );
  }
  if (style === "tracker") {
    return (
      <div style={{ width: 816, height: 1056, background: "#0B1120", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", top: -190, left: "50%", transform: "translateX(-50%)", width: 620, height: 620, borderRadius: "50%", background: d.accent, opacity: 0.12 }} />
        <div style={{ paddingTop: 46, display: "flex", alignItems: "center", gap: 16, position: "relative" }}>
          <Logo src={d.logo} size={70} />
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(34, d.name, 20), color: "#fff", letterSpacing: -0.5 }}>{d.name}</div>
        </div>
        <div style={{ fontFamily: TALL, fontSize: 72, color: "#fff", letterSpacing: 2, marginTop: 26, lineHeight: 1, textTransform: "uppercase", position: "relative" }}>
          Track us <span style={{ color: d.accent }}>live</span>
        </div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: fit(22, d.headline, 36), color: "rgba(255,255,255,0.75)", marginTop: 12, padding: "0 90px", lineHeight: 1.35, position: "relative" }}>{d.headline}</div>
        <div style={{ marginTop: 34, position: "relative" }}><PhoneMock d={d} w={272} /></div>
        <div style={{ display: "flex", alignItems: "center", gap: 26, marginTop: 34, position: "relative" }}>
          <QRCard qr={d.qr} size={148} pad={12} radius={18} />
          <div style={{ textAlign: "left" }}>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 24, color: "#fff", lineHeight: 1.15 }}>Scan with your<br />phone camera</div>
            <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: 16, color: d.accent, marginTop: 8 }}>{d.url || "thetrucktrackr.com"}</div>
          </div>
        </div>
        <div style={{ marginTop: "auto", width: "100%", background: "rgba(255,255,255,0.06)", padding: "13px 24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontFamily: DISPLAY }}>
          <img src="/icon-192.png" alt="" style={{ width: 26, height: 26, borderRadius: 7 }} />
          <span style={{ fontWeight: 800, fontSize: 16, color: "#fff" }}>TruckTrackr</span>
          <span style={{ fontWeight: 600, fontSize: 14, color: "rgba(255,255,255,0.55)" }}>· Free on the App Store & Google Play</span>
        </div>
      </div>
    );
  }
  if (style === "photo") {
    return (
      <div style={{ width: 816, height: 1056, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ position: "relative", height: 430, flexShrink: 0, background: "#1e293b" }}>
          {d.cover && <img src={d.cover} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(2,6,23,0.82) 0%, rgba(2,6,23,0.12) 55%, rgba(2,6,23,0) 100%)" }} />
          <div style={{ position: "absolute", left: 40, right: 40, bottom: 28, display: "flex", alignItems: "center", gap: 22 }}>
            <Logo src={d.logo} size={104} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: nameSize, lineHeight: 1.02, color: "#fff", letterSpacing: -1 }}>{d.name}</div>
              {d.tagline && <div style={{ fontFamily: DISPLAY, fontSize: 20, color: "rgba(255,255,255,0.88)", marginTop: 6, fontWeight: 500 }}>{d.tagline}</div>}
            </div>
          </div>
        </div>
        <div style={{ flex: 1, padding: "44px 56px 0", display: "flex", flexDirection: "column" }}>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(42, d.headline, 30), color: d.accent, lineHeight: 1.1, letterSpacing: -0.5 }}>{d.headline}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 48, marginTop: 40, flex: 1 }}>
            <div style={{ border: `4px solid ${d.accent}`, borderRadius: 26, padding: 18, lineHeight: 0, background: "#fff" }}>
              {d.qr ? <img src={d.qr} alt="QR code" style={{ width: 264, height: 264, display: "block" }} /> : <QRCard qr={null} size={264} pad={0} />}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 24, color: "#0f172a" }}>Scan with your phone camera</div>
              {FLYER_BULLETS.map((b) => <Bullet key={b} accent={d.accent} size={19}>{b}</Bullet>)}
              {d.instagram && <div style={{ fontFamily: DISPLAY, fontSize: 17, fontWeight: 600, color: "#64748b" }}>@{d.instagram} on Instagram</div>}
            </div>
          </div>
        </div>
        <Footer accent={d.accent} url={d.url} size={17} />
      </div>
    );
  }
  if (style === "bold") {
    return (
      <div style={{ width: 816, height: 1056, background: d.accent, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden" }}>
        <div style={{ paddingTop: 64 }}><Logo src={d.logo} size={140} /></div>
        <div style={{ fontFamily: TALL, fontSize: fit(110, d.name, 14), color: "#fff", lineHeight: 0.98, letterSpacing: 2, padding: "28px 48px 0", textTransform: "uppercase" }}>{d.name}</div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: fit(30, d.headline, 34), color: "rgba(255,255,255,0.94)", padding: "18px 72px 0", lineHeight: 1.25 }}>{d.headline}</div>
        <div style={{ marginTop: 42 }}><QRCard qr={d.qr} size={296} pad={22} radius={26} /></div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 22, color: "#fff", marginTop: 26, letterSpacing: 1.5, textTransform: "uppercase" }}>Scan for our live schedule</div>
        <div style={{ marginTop: "auto", width: "100%" }}>
          <div style={{ background: "rgba(0,0,0,0.28)", padding: "16px 24px", fontFamily: DISPLAY, fontWeight: 700, fontSize: 18, color: "#fff" }}>
            {d.url || "thetrucktrackr.com"} · Powered by TruckTrackr
          </div>
        </div>
      </div>
    );
  }
  return ( /* clean */
    <div style={{ width: 816, height: 1056, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden" }}>
      <div style={{ height: 14, width: "100%", background: d.accent, flexShrink: 0 }} />
      <div style={{ paddingTop: 56 }}><Logo src={d.logo} size={128} ring="#f1f5f9" /></div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(58, d.name, 18), color: "#0f172a", letterSpacing: -1.2, padding: "26px 56px 0", lineHeight: 1.05 }}>{d.name}</div>
      {d.tagline && <div style={{ fontFamily: DISPLAY, fontSize: 20, color: "#64748b", marginTop: 10, fontWeight: 500, padding: "0 64px" }}>{d.tagline}</div>}
      <div style={{ width: 72, height: 5, background: d.accent, borderRadius: 3, marginTop: 30 }} />
      <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: fit(30, d.headline, 34), color: "#334155", marginTop: 30, padding: "0 72px", lineHeight: 1.3 }}>{d.headline}</div>
      <div style={{ marginTop: 40, position: "relative", padding: 24 }}>
        <span style={{ position: "absolute", top: 0, left: 0, width: 44, height: 44, borderTop: `5px solid ${d.accent}`, borderLeft: `5px solid ${d.accent}` }} />
        <span style={{ position: "absolute", top: 0, right: 0, width: 44, height: 44, borderTop: `5px solid ${d.accent}`, borderRight: `5px solid ${d.accent}` }} />
        <span style={{ position: "absolute", bottom: 0, left: 0, width: 44, height: 44, borderBottom: `5px solid ${d.accent}`, borderLeft: `5px solid ${d.accent}` }} />
        <span style={{ position: "absolute", bottom: 0, right: 0, width: 44, height: 44, borderBottom: `5px solid ${d.accent}`, borderRight: `5px solid ${d.accent}` }} />
        {d.qr ? <img src={d.qr} alt="QR code" style={{ width: 280, height: 280, display: "block" }} /> : <QRCard qr={null} size={280} pad={0} />}
      </div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 19, color: "#0f172a", marginTop: 24 }}>Scan to find us · {d.url || "thetrucktrackr.com"}</div>
      <div style={{ marginTop: "auto", width: "100%", padding: "0 0 0" }}>
        <div style={{ fontFamily: DISPLAY, fontSize: 14, color: "#94a3b8", padding: "0 0 18px" }}>Powered by TruckTrackr — live food truck tracking</div>
        <div style={{ height: 14, width: "100%", background: d.accent }} />
      </div>
    </div>
  );
}

function Sign({ style, d }) {
  if (style === "bold") {
    return (
      <div style={{ width: 480, height: 672, background: d.accent, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden" }}>
        <div style={{ paddingTop: 36 }}><Logo src={d.logo} size={88} /></div>
        <div style={{ fontFamily: TALL, fontSize: fit(58, d.name, 14), color: "#fff", letterSpacing: 1.5, padding: "16px 28px 0", lineHeight: 1, textTransform: "uppercase" }}>{d.name}</div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 17, color: "rgba(255,255,255,0.92)", padding: "10px 36px 0", lineHeight: 1.3 }}>{d.headline}</div>
        <div style={{ marginTop: 24 }}><QRCard qr={d.qr} size={208} pad={16} /></div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 16, color: "#fff", marginTop: 18, letterSpacing: 1.2, textTransform: "uppercase" }}>Scan me</div>
        <div style={{ marginTop: "auto", width: "100%", background: "rgba(0,0,0,0.28)", padding: "10px 16px", fontFamily: DISPLAY, fontWeight: 700, fontSize: 13, color: "#fff" }}>{d.url || "thetrucktrackr.com"}</div>
      </div>
    );
  }
  if (style === "photo") {
    return (
      <div style={{ width: 480, height: 672, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ position: "relative", height: 236, flexShrink: 0, background: "#1e293b" }}>
          {d.cover && <img src={d.cover} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(2,6,23,0.78), rgba(2,6,23,0) 60%)" }} />
          <div style={{ position: "absolute", left: 22, bottom: 16, right: 22, display: "flex", alignItems: "center", gap: 14 }}>
            <Logo src={d.logo} size={62} />
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(30, d.name, 16), color: "#fff", lineHeight: 1.05, letterSpacing: -0.5 }}>{d.name}</div>
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "24px 28px 0" }}>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(22, d.headline, 30), color: d.accent, lineHeight: 1.2 }}>{d.headline}</div>
          <div style={{ marginTop: 18, border: `3px solid ${d.accent}`, borderRadius: 18, padding: 12, lineHeight: 0, background: "#fff" }}>
            {d.qr ? <img src={d.qr} alt="QR code" style={{ width: 190, height: 190, display: "block" }} /> : <QRCard qr={null} size={190} pad={0} />}
          </div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, color: "#0f172a", marginTop: 14 }}>Scan with your phone camera</div>
        </div>
        <Footer accent={d.accent} url={d.url} size={13} />
      </div>
    );
  }
  return ( /* clean */
    <div style={{ width: 480, height: 672, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden" }}>
      <div style={{ height: 10, width: "100%", background: d.accent }} />
      <div style={{ paddingTop: 32 }}><Logo src={d.logo} size={84} ring="#f1f5f9" /></div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(32, d.name, 18), color: "#0f172a", letterSpacing: -0.8, padding: "16px 28px 0", lineHeight: 1.05 }}>{d.name}</div>
      <div style={{ width: 48, height: 4, background: d.accent, borderRadius: 2, marginTop: 16 }} />
      <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: fit(17, d.headline, 34), color: "#475569", marginTop: 16, padding: "0 36px", lineHeight: 1.35 }}>{d.headline}</div>
      <div style={{ marginTop: 22 }}><QRCard qr={d.qr} size={200} pad={14} /></div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 14, color: "#0f172a", marginTop: 16 }}>{d.url || "thetrucktrackr.com"}</div>
      <div style={{ marginTop: "auto", width: "100%", height: 10, background: d.accent }} />
    </div>
  );
}

function Square({ style, d }) {
  if (style === "photo") {
    return (
      <div style={{ width: 540, height: 540, position: "relative", background: "#1e293b", overflow: "hidden" }}>
        {d.cover && <img src={d.cover} alt="" crossOrigin="anonymous" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(2,6,23,0.88) 0%, rgba(2,6,23,0.28) 55%, rgba(2,6,23,0.18) 100%)" }} />
        <div style={{ position: "absolute", top: 24, left: 24, display: "flex", alignItems: "center", gap: 12 }}>
          <Logo src={d.logo} size={56} />
          <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 20, color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>{d.name}</span>
        </div>
        <div style={{ position: "absolute", left: 28, right: 190, bottom: 30 }}>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(34, d.headline, 26), color: "#fff", lineHeight: 1.12, letterSpacing: -0.5 }}>{d.headline}</div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, color: "#fff", marginTop: 12, background: d.accent, display: "inline-block", padding: "6px 14px", borderRadius: 999 }}>{d.url || "thetrucktrackr.com"}</div>
        </div>
        <div style={{ position: "absolute", right: 24, bottom: 26 }}><QRCard qr={d.qr} size={118} pad={10} radius={14} /></div>
      </div>
    );
  }
  if (style === "bold") {
    return (
      <div style={{ width: 540, height: 540, background: d.accent, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ paddingTop: 38 }}><Logo src={d.logo} size={84} /></div>
        <div style={{ fontFamily: TALL, fontSize: fit(56, d.name, 14), color: "#fff", letterSpacing: 1.5, padding: "14px 32px 0", lineHeight: 1, textTransform: "uppercase" }}>{d.name}</div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: fit(19, d.headline, 32), color: "rgba(255,255,255,0.94)", padding: "10px 48px 0", lineHeight: 1.3 }}>{d.headline}</div>
        <div style={{ marginTop: 20 }}><QRCard qr={d.qr} size={150} pad={12} /></div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 15, color: "#fff", marginTop: "auto", paddingBottom: 22 }}>{d.url || "thetrucktrackr.com"}</div>
      </div>
    );
  }
  return ( /* clean */
    <div style={{ width: 540, height: 540, background: "#fff", border: `12px solid ${d.accent}`, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden", boxSizing: "border-box" }}>
      <div style={{ paddingTop: 32 }}><Logo src={d.logo} size={78} ring="#f1f5f9" /></div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(30, d.name, 18), color: "#0f172a", letterSpacing: -0.6, padding: "14px 32px 0", lineHeight: 1.05 }}>{d.name}</div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: fit(16, d.headline, 34), color: "#475569", marginTop: 10, padding: "0 44px", lineHeight: 1.35 }}>{d.headline}</div>
      <div style={{ marginTop: 18 }}><QRCard qr={d.qr} size={148} pad={10} /></div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 14, color: d.accent, marginTop: "auto", paddingBottom: 20 }}>{d.url || "thetrucktrackr.com"}</div>
    </div>
  );
}

function Story({ style, d }) {
  if (style === "photo") {
    return (
      <div style={{ width: 540, height: 960, position: "relative", background: "#1e293b", overflow: "hidden" }}>
        {d.cover && <img src={d.cover} alt="" crossOrigin="anonymous" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(2,6,23,0.92) 0%, rgba(2,6,23,0.35) 45%, rgba(2,6,23,0.45) 100%)" }} />
        <div style={{ position: "absolute", top: 46, left: 0, right: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <Logo src={d.logo} size={92} />
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(38, d.name, 16), color: "#fff", textAlign: "center", padding: "0 32px", lineHeight: 1.08, textShadow: "0 2px 10px rgba(0,0,0,0.45)" }}>{d.name}</div>
        </div>
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 60, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 20, padding: "0 36px" }}>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(30, d.headline, 30), color: "#fff", lineHeight: 1.2 }}>{d.headline}</div>
          <QRCard qr={d.qr} size={180} pad={14} />
          <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 17, color: "#fff", background: d.accent, padding: "9px 22px", borderRadius: 999 }}>{d.url || "thetrucktrackr.com"}</div>
        </div>
      </div>
    );
  }
  if (style === "bold") {
    return (
      <div style={{ width: 540, height: 960, background: d.accent, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden" }}>
        <div style={{ paddingTop: 72 }}><Logo src={d.logo} size={116} /></div>
        <div style={{ fontFamily: TALL, fontSize: fit(76, d.name, 14), color: "#fff", letterSpacing: 2, padding: "24px 36px 0", lineHeight: 0.98, textTransform: "uppercase" }}>{d.name}</div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: fit(24, d.headline, 32), color: "rgba(255,255,255,0.94)", padding: "18px 52px 0", lineHeight: 1.3 }}>{d.headline}</div>
        <div style={{ marginTop: 46 }}><QRCard qr={d.qr} size={230} pad={18} radius={22} /></div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 18, color: "#fff", marginTop: 22, letterSpacing: 1.2, textTransform: "uppercase" }}>Scan for live locations</div>
        <div style={{ marginTop: "auto", width: "100%", background: "rgba(0,0,0,0.28)", padding: "16px", fontFamily: DISPLAY, fontWeight: 700, fontSize: 16, color: "#fff" }}>{d.url || "thetrucktrackr.com"}</div>
      </div>
    );
  }
  return ( /* clean */
    <div style={{ width: 540, height: 960, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", overflow: "hidden" }}>
      <div style={{ height: 16, width: "100%", background: d.accent }} />
      <div style={{ paddingTop: 64 }}><Logo src={d.logo} size={108} ring="#f1f5f9" /></div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(42, d.name, 18), color: "#0f172a", letterSpacing: -1, padding: "24px 40px 0", lineHeight: 1.05 }}>{d.name}</div>
      <div style={{ width: 60, height: 5, background: d.accent, borderRadius: 3, marginTop: 26 }} />
      <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: fit(22, d.headline, 34), color: "#475569", marginTop: 26, padding: "0 52px", lineHeight: 1.4 }}>{d.headline}</div>
      <div style={{ marginTop: 40 }}><QRCard qr={d.qr} size={220} pad={16} /></div>
      <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 17, color: "#0f172a", marginTop: 24 }}>{d.url || "thetrucktrackr.com"}</div>
      <div style={{ marginTop: "auto", width: "100%", height: 16, background: d.accent }} />
    </div>
  );
}

function Stickers({ style, d }) {
  const light = style === "light";
  const cell = (i) => (
    <div key={i} style={{ width: 240, height: 240, display: "grid", placeItems: "center" }}>
      <div style={{ width: 228, height: 228, borderRadius: "50%", border: "2px dashed #cbd5e1", display: "grid", placeItems: "center" }}>
        <div style={{
          width: 212, height: 212, borderRadius: "50%", overflow: "hidden", boxSizing: "border-box",
          background: light ? "#fff" : d.accent, border: light ? `7px solid ${d.accent}` : "none",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center",
        }}>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: fit(15, d.name, 18), color: light ? "#0f172a" : "#fff", padding: "0 30px", lineHeight: 1.1 }}>{d.name}</div>
          <div style={{ marginTop: 8, background: "#fff", padding: 6, borderRadius: 10, lineHeight: 0 }}>
            {d.qr ? <img src={d.qr} alt="QR" style={{ width: 96, height: 96, display: "block" }} /> : <div style={{ width: 96, height: 96, background: "#f1f5f9" }} />}
          </div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 11, letterSpacing: 1.4, marginTop: 7, color: light ? d.accent : "#fff", textTransform: "uppercase" }}>Scan to find us</div>
        </div>
      </div>
    </div>
  );
  return (
    <div style={{ width: 816, height: 1056, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, padding: "22px 0 4px", fontFamily: DISPLAY }}>
        <span style={{ fontWeight: 800, fontSize: 19, color: "#0f172a" }}>{d.name} — QR stickers</span>
        <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>2.5" round · cut along dashed lines</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 240px)", gap: 8, paddingTop: 6 }}>
        {Array.from({ length: 12 }, (_, i) => cell(i))}
      </div>
    </div>
  );
}

function Material({ material, style, d }) {
  if (material === "flyer") return <Flyer style={style} d={d} />;
  if (material === "sign") return <Sign style={style} d={d} />;
  if (material === "square") return <Square style={style} d={d} />;
  if (material === "story") return <Story style={style} d={d} />;
  return <Stickers style={style} d={d} />;
}

/* ------------------------------------------------------------------ */
/* Upgrade wall                                                        */
/* ------------------------------------------------------------------ */

const UpgradeWall = () => (
  <div className="mx-auto max-w-2xl px-4 py-12" data-testid="marketing-kit-upgrade-wall">
    <div className="rounded-3xl border bg-card p-8 sm:p-10 text-center">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center">
        <LockKey weight="duotone" className="h-7 w-7 text-primary" />
      </div>
      <h1 className="mt-5 font-display font-bold text-2xl sm:text-3xl tracking-tight">Marketing Kit is a Pro feature</h1>
      <p className="mt-3 text-muted-foreground">
        Generate print-ready flyers, counter signs, social graphics and QR sticker sheets —
        automatically branded with your logo, photos and a scannable QR code that sends
        customers straight to your live schedule.
      </p>
      <ul className="mt-6 text-left inline-block space-y-2 text-sm font-medium">
        {["8.5\u00d711\" flyers & 5\u00d77\" counter signs (PDF & PNG)", "Instagram post + story graphics", "Sheet of 12 QR stickers for cups & windows", "Real scannable QR codes \u2014 always up to date"].map((f) => (
          <li key={f} className="flex items-center gap-2"><Sparkle weight="fill" className="h-4 w-4 text-primary flex-none" />{f}</li>
        ))}
      </ul>
      <div className="mt-8">
        <Button asChild className="rounded-full h-11 px-7 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="marketing-kit-upgrade-cta">
          <Link to="/pricing">Upgrade to Pro</Link>
        </Button>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function MarketingKit() {
  const { user, loading: authLoading } = useAuth();
  const [gate, setGate] = useState("loading"); // loading | pro | wall | error
  const [trucks, setTrucks] = useState([]);
  const [truckId, setTruckId] = useState(null);
  const [kit, setKit] = useState(null);
  const [assets, setAssets] = useState({ logo: null, cover: null, qr: null });
  const [material, setMaterial] = useState("flyer");
  const [styleKey, setStyleKey] = useState("photo");
  const [accent, setAccent] = useState(ACCENTS[1]);
  const [headline, setHeadline] = useState("");
  const [downloading, setDownloading] = useState(null); // "png" | "pdf"
  const exportRef = useRef(null);
  const previewWrapRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.4);
  const prefsLoaded = useRef(false);
  const saveTimer = useRef(null);

  const mat = MAT_BY_KEY[material];
  const style = mat.styles.includes(styleKey) ? styleKey : mat.styles[0];

  /* gate + trucks */
  useEffect(() => {
    if (authLoading) return;
    if (!user) { setGate("error"); return; }
    (async () => {
      try {
        const { data } = await api.get("/marketing-kit/trucks");
        setTrucks(data.trucks || []);
        if (!data.is_pro) { setGate("wall"); return; }
        setTruckId((data.trucks || [])[0]?.truck_id || null);
        setGate("pro");
      } catch {
        setGate("error");
      }
    })();
  }, [authLoading, user]);

  /* kit data + prefs */
  useEffect(() => {
    if (gate !== "pro") return;
    prefsLoaded.current = false;
    const qp = truckId ? `?truck_id=${encodeURIComponent(truckId)}` : "";
    (async () => {
      try {
        const { data } = await api.get(`/marketing-kit/data${qp}`);
        setKit(data);
        const p = data.prefs || {};
        if (p.accent_color) setAccent(p.accent_color);
        if (p.material && MAT_BY_KEY[p.material]) setMaterial(p.material);
        if (p.style) setStyleKey(p.style);
        setHeadline(p.headline || (data.headlines?.[0] || "Find & follow {truck}").replace(/\{truck\}/g, data.truck_name));
        trackSiteEvent("marketing_kit_view");
        const jobs = [];
        jobs.push(data.logo_url ? toDataUrl(data.logo_url).catch(() => null) : Promise.resolve(null));
        jobs.push(data.cover_url ? toDataUrl(data.cover_url).catch(() => null) : Promise.resolve(null));
        jobs.push(data.profile_url ? toDataUrl(`${API}/marketing-kit/qr.png?scale=20${truckId ? `&truck_id=${encodeURIComponent(truckId)}` : ""}`).catch(() => null) : Promise.resolve(null));
        const [logo, cover, qr] = await Promise.all(jobs);
        setAssets({ logo, cover, qr });
        setTimeout(() => { prefsLoaded.current = true; }, 50);
      } catch (e) {
        if (e?.response?.data?.detail === "pro_required") setGate("wall");
        else if (e?.response?.status === 404) setKit({ missing: true });
        else toast.error("Couldn't load your Marketing Kit data");
      }
    })();
  }, [gate, truckId]);

  /* debounced prefs autosave */
  useEffect(() => {
    if (!prefsLoaded.current || gate !== "pro") return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const qp = truckId ? `?truck_id=${encodeURIComponent(truckId)}` : "";
      api.put(`/marketing-kit/prefs${qp}`, { accent_color: accent, headline, material, style }).catch(() => {});
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [accent, headline, material, style, gate, truckId]);

  /* responsive preview scale */
  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    const compute = () => setPreviewScale(Math.min(0.62, Math.max(0.2, (el.clientWidth - 8) / mat.w)));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mat.w, gate, kit]);

  const templateData = useMemo(() => kit && !kit.missing ? {
    name: kit.truck_name,
    headline: headline || "Scan to see where we'll be next",
    accent,
    logo: assets.logo,
    cover: assets.cover,
    qr: assets.qr,
    url: kit.profile_url_display,
    tagline: kit.tagline,
    instagram: igHandle(kit.instagram),
    nextStop: kit.next_stop,
  } : null, [kit, headline, accent, assets]);

  const download = useCallback(async (format) => {
    if (!exportRef.current || downloading) return;
    setDownloading(format);
    try {
      await document.fonts.ready;
      const canvas = await html2canvas(exportRef.current, {
        scale: mat.scale, useCORS: true, backgroundColor: "#ffffff", logging: false,
      });
      const base = `${slugify(kit?.truck_name)}-${material}-${style}`;
      if (format === "png") {
        await new Promise((resolve) => canvas.toBlob((blob) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `${base}.png`;
          a.click();
          setTimeout(() => URL.revokeObjectURL(a.href), 4000);
          resolve();
        }, "image/png"));
      } else {
        const pdf = new jsPDF({ orientation: mat.pdf[0] > mat.pdf[1] ? "landscape" : "portrait", unit: "in", format: mat.pdf });
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.93), "JPEG", 0, 0, mat.pdf[0], mat.pdf[1]);
        pdf.save(`${base}.pdf`);
      }
      trackSiteEvent("marketing_kit_download", { material, style, format });
      toast.success(format === "png" ? "PNG downloaded" : "Print-ready PDF downloaded");
    } catch (e) {
      toast.error("Download failed — please try again");
    } finally {
      setDownloading(null);
    }
  }, [downloading, mat, material, style, kit]);

  /* ---------------- render states ---------------- */

  if (authLoading || gate === "loading") {
    return (
      <div className="grid place-items-center py-32" data-testid="marketing-kit-loading">
        <CircleNotch className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (gate === "error") {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="marketing-kit-error">
        <p className="text-muted-foreground">Please <Link to="/login" className="text-primary font-semibold underline">log in</Link> as a truck owner to use the Marketing Kit.</p>
      </div>
    );
  }
  if (gate === "wall") return <UpgradeWall />;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8" data-testid="marketing-kit-page">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="marketing-kit-back-link">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>
        <span className="text-muted-foreground/40">/</span>
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-2.5">
          <Megaphone weight="duotone" className="h-7 w-7 text-primary" /> Marketing Kit
          <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary rounded-full px-2 py-0.5">Pro</span>
        </h1>
        {trucks.length > 1 && (
          <select
            value={truckId || ""}
            onChange={(e) => setTruckId(e.target.value)}
            className="ml-auto h-9 rounded-lg border bg-background px-3 text-sm font-medium"
            data-testid="marketing-kit-truck-select"
          >
            {trucks.map((t) => <option key={t.truck_id} value={t.truck_id}>{t.truck_name}</option>)}
          </select>
        )}
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Print-ready flyers, signs, social graphics & stickers — auto-branded from your profile with a real scannable QR code.
      </p>

      {kit?.missing ? (
        <div className="mt-8 rounded-2xl border border-dashed p-10 text-center" data-testid="marketing-kit-no-profile">
          <p className="font-semibold">No truck profile found yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Set up your truck profile first, then come back to generate materials.</p>
        </div>
      ) : !kit ? (
        <div className="grid place-items-center py-24"><CircleNotch className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <>
          {!kit.profile_url && (
            <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm font-medium" data-testid="marketing-kit-unpublished-banner">
              Your public profile isn't published yet — QR codes activate once it's live. Materials below show a placeholder QR.
            </div>
          )}

          {/* material picker */}
          <div className="mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="marketing-kit-material-picker">
            {MATERIALS.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => { setMaterial(m.key); if (!m.styles.includes(styleKey)) setStyleKey(m.styles[0]); }}
                data-testid={`marketing-kit-material-${m.key}`}
                className={`flex-none rounded-xl border px-4 py-2.5 text-left transition-colors ${material === m.key ? "border-primary bg-primary/5" : "hover:border-primary/40"}`}
              >
                <div className="font-semibold text-sm">{m.label}</div>
                <div className="text-[11px] text-muted-foreground">{m.sub}</div>
              </button>
            ))}
          </div>

          <div className="mt-5 grid gap-6 lg:grid-cols-[340px_1fr]">
            {/* controls */}
            <div className="space-y-5">
              <div className="rounded-2xl border bg-card p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Sparkle weight="duotone" className="h-3.5 w-3.5" /> Style</div>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {mat.styles.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStyleKey(s)}
                      data-testid={`marketing-kit-style-${s}`}
                      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${style === s ? "border-primary bg-primary text-primary-foreground" : "hover:border-primary/50"}`}
                    >
                      {STYLE_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border bg-card p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Palette weight="duotone" className="h-3.5 w-3.5" /> Accent color</div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {ACCENTS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setAccent(c)}
                      data-testid={`marketing-kit-accent-${c.replace("#", "")}`}
                      className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${accent === c ? "border-foreground scale-110" : "border-transparent"}`}
                      style={{ background: c }}
                      aria-label={c}
                    />
                  ))}
                  <label className="h-8 w-8 rounded-full border-2 border-dashed grid place-items-center cursor-pointer overflow-hidden relative" title="Custom color">
                    <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="marketing-kit-accent-custom" />
                    <span className="text-xs font-bold text-muted-foreground">+</span>
                  </label>
                </div>
              </div>

              {material !== "stickers" && (
                <div className="rounded-2xl border bg-card p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Headline</div>
                  <input
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value.slice(0, 80))}
                    className="mt-2.5 w-full h-10 rounded-lg border bg-background px-3 text-sm font-medium"
                    data-testid="marketing-kit-headline-input"
                  />
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {(kit.headlines || []).map((h, i) => {
                      const txt = h.replace(/\{truck\}/g, kit.truck_name);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setHeadline(txt)}
                          data-testid={`marketing-kit-headline-preset-${i}`}
                          className="rounded-full border px-3 py-1 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
                        >
                          {txt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="rounded-2xl border bg-card p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><DownloadSimple weight="duotone" className="h-3.5 w-3.5" /> Download</div>
                <div className="mt-3 flex flex-col gap-2">
                  <Button
                    onClick={() => download("png")}
                    disabled={!!downloading}
                    className="rounded-full h-10 bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
                    data-testid="marketing-kit-download-png"
                  >
                    {downloading === "png" ? <CircleNotch className="h-4 w-4 animate-spin" /> : <DownloadSimple weight="bold" className="h-4 w-4" />}
                    Download PNG {material === "square" || material === "story" ? "(1080px)" : "(high-res)"}
                  </Button>
                  {mat.pdf && (
                    <Button
                      onClick={() => download("pdf")}
                      disabled={!!downloading}
                      variant="outline"
                      className="rounded-full h-10 gap-2"
                      data-testid="marketing-kit-download-pdf"
                    >
                      {downloading === "pdf" ? <CircleNotch className="h-4 w-4 animate-spin" /> : <FilePdf weight="duotone" className="h-4 w-4" />}
                      Print-ready PDF ({mat.pdf[0]}×{mat.pdf[1]}")
                    </Button>
                  )}
                </div>
                {kit.profile_url && (
                  <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed flex items-start gap-1.5">
                    <QrCode className="h-3.5 w-3.5 flex-none mt-0.5" />
                    QR code links to {kit.profile_url_display} — scans always show your live schedule.
                  </p>
                )}
              </div>
            </div>

            {/* preview */}
            <div ref={previewWrapRef} className="rounded-2xl border bg-muted/30 p-4 grid place-items-center min-h-[420px]" data-testid="marketing-kit-preview">
              {templateData ? (
                <div style={{ width: mat.w * previewScale, height: mat.h * previewScale, position: "relative" }} className="shadow-xl rounded-sm overflow-hidden">
                  <div style={{ transform: `scale(${previewScale})`, transformOrigin: "top left", width: mat.w, height: mat.h }}>
                    <Material material={material} style={style} d={templateData} />
                  </div>
                </div>
              ) : (
                <CircleNotch className="h-7 w-7 animate-spin text-primary" />
              )}
            </div>
          </div>

          {/* offscreen export node at native size */}
          {templateData && (
            <div style={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }} aria-hidden="true">
              <div ref={exportRef} style={{ width: mat.w, height: mat.h }}>
                <Material material={material} style={style} d={templateData} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
